/*
 * Lily & Minx - cookie banner (module lm-consent)
 * LAST UPDATED: 28/09/2026
 * ---------------------------------------------------------------------------
 * <lm-consent> (sections/lm-consent.liquid, overlay group) asks the visitors
 * who need asking, records the choice with Shopify's Customer Privacy API and
 * opens again from any link to #cookie-settings.
 *
 * Who is asked, once the API has loaded and only while no choice is recorded
 * (currentVisitorConsent() blank for analytics, marketing and preferences):
 * - shouldShowBanner() is true: Shopify's flag for the regions chosen in
 *   Settings > Customer privacy > Cookie banner; or
 * - getRegion() puts the visitor in the EU, EEA, UK or Switzerland, a safety
 *   net in case those regions are ever unticked in Admin.
 *
 * What a choice records: setTrackingConsent({ analytics, marketing,
 * preferences }). Accept all and Reject all set all three. Save choices sets
 * the two switches, and preferences follows Analytics, because the privacy
 * policy names three kinds of cookie (essential, analytics, advertising).
 * Shopify then runs or holds its pixels, the Google & YouTube app sends the
 * choice to Google as consent mode, and the Clarity embed forwards Shopify's
 * visitorConsentCollected event to Clarity. This file loads and blocks no tag
 * itself. The banner closes at once; if the save fails, nothing is recorded
 * and the banner asks again on the next page.
 *
 * While it is open, <html> carries lm-consent-open and lm-consent.css hides
 * the floating app widgets, so they can't cover its buttons (the app-layering
 * rule in design-system/patterns.md).
 *
 * Theme editor: it shows while its section is selected and records nothing.
 * Other scripts can open Cookie settings with
 * document.dispatchEvent(new Event('lm:consent:open')).
 */

const PURPOSES = ['analytics', 'marketing', 'preferences'];
const SWITCHES = ['analytics', 'marketing'];
const OPEN_HASH = '#cookie-settings';
const OPEN_EVENT = 'lm:consent:open';
const ROOT_CLASS = 'lm-consent-open';

/* ISO 3166-1 alpha-2: the EU 27, then Iceland, Liechtenstein and Norway (EEA), the UK and Switzerland. */
const ASK_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU',
  'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
  'IS', 'LI', 'NO',
  'GB', 'CH',
]);

/**
 * Whether this visitor should be asked now.
 * @param {object | null} api - window.Shopify.customerPrivacy
 * @returns {boolean}
 */
export const shouldAsk = (api) => {
  if (!api) return false;
  const consent = api.currentVisitorConsent?.() ?? {};
  if (PURPOSES.some((purpose) => consent[purpose] === 'yes' || consent[purpose] === 'no')) return false;
  if (api.shouldShowBanner?.()) return true;
  /* getRegion() is ISO 3166-2 without the hyphen (AUNSW, DEBE): the country is the first two letters. */
  const country = String(api.getRegion?.() ?? '').slice(0, 2).toUpperCase();
  return ASK_COUNTRIES.has(country);
};

/**
 * The consent a button records. Anything but accept or save records a refusal.
 * @param {string} action - accept, reject or save
 * @param {{ analytics?: boolean, marketing?: boolean }} [switches] - the switches, for save
 * @returns {{ analytics: boolean, marketing: boolean, preferences: boolean }}
 */
export const consentFor = (action, switches = {}) => {
  if (action === 'accept') return { analytics: true, marketing: true, preferences: true };
  if (action === 'save') {
    const analytics = switches.analytics === true;
    return { analytics, marketing: switches.marketing === true, preferences: analytics };
  }
  return { analytics: false, marketing: false, preferences: false };
};

/** @type {Promise<object | null> | null} */
let privacyApi = null;

/**
 * Shopify's Customer Privacy API (window.Shopify.customerPrivacy), loaded once.
 * content_for_header defines Shopify.loadFeatures as a queue, so this works as soon as the page runs scripts.
 * @returns {Promise<object | null>} null when it cannot load
 */
const loadPrivacyApi = () => {
  privacyApi ??= new Promise((resolve) => {
    const shopify = window.Shopify;
    if (typeof shopify?.loadFeatures !== 'function') {
      resolve(null);
      return;
    }
    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) console.error('lm-consent: the Customer Privacy API did not load', error);
      resolve(error ? null : window.Shopify.customerPrivacy ?? null);
    });
  });
  return privacyApi;
};

class LmConsent extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {object | null} */
  #api = null;

  /** Opened because a choice is needed: then it closes only with a choice. */
  #asking = false;

  /** @type {HTMLElement | null} gets focus back when Cookie settings closes */
  #returnFocus = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const choices = this.querySelector('.lm-consent__choices');
    if (choices) this.#button('settings')?.setAttribute('aria-controls', choices.id);

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    document.addEventListener('click', (event) => this.#onLinkClick(event), { signal });
    document.addEventListener(OPEN_EVENT, () => this.open(), { signal });
    window.addEventListener('hashchange', () => {
      if (window.location.hash === OPEN_HASH) this.open();
    }, { signal });

    if (window.Shopify?.designMode) {
      const own = (event) => event.detail?.sectionId === this.dataset.sectionId;
      document.addEventListener('shopify:section:select', (event) => own(event) && this.#show('banner'), { signal });
      document.addEventListener('shopify:section:load', (event) => own(event) && this.#show('banner'), { signal });
      document.addEventListener('shopify:section:deselect', (event) => own(event) && this.#hide(), { signal });
      return;
    }

    loadPrivacyApi().then((api) => {
      if (!this.isConnected) return;
      this.#api = api;
      if (window.location.hash === OPEN_HASH) {
        this.open();
      } else if (shouldAsk(api)) {
        this.#asking = true;
        this.#show('banner');
      }
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    document.documentElement.classList.remove(ROOT_CLASS);
  }

  /**
   * Cookie settings: the switches, set to the recorded choice.
   * @param {Element | null} [trigger] - gets focus back on close
   */
  async open(trigger = document.activeElement) {
    this.#returnFocus = trigger instanceof HTMLElement && trigger !== document.body && !this.contains(trigger) ? trigger : null;
    if (!window.Shopify?.designMode) this.#api ??= await loadPrivacyApi();
    this.#show('settings');
    this.querySelector('.lm-consent__panel')?.focus({ preventScroll: true });
  }

  /** @param {'banner' | 'settings'} view */
  #show(view) {
    const settings = view === 'settings';
    this.dataset.view = view;
    if (settings) this.#syncSwitches();

    const choices = this.querySelector('.lm-consent__choices');
    if (choices) choices.hidden = !settings;
    this.#button('settings')?.setAttribute('aria-expanded', String(settings));
    const close = this.#button('close');
    if (close) close.hidden = this.#asking;

    this.hidden = false;
    document.documentElement.classList.add(ROOT_CLASS);
  }

  #hide() {
    const hadFocus = this.contains(document.activeElement);
    this.hidden = true;
    this.#asking = false;
    document.documentElement.classList.remove(ROOT_CLASS);

    /* Drop #cookie-settings, so a reload doesn't reopen it and the same link works again. */
    if (window.location.hash === OPEN_HASH) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }

    if (hadFocus && this.#returnFocus?.isConnected) this.#returnFocus.focus();
    this.#returnFocus = null;
  }

  /** Ticks each switch whose purpose the visitor accepted; with no choice recorded, none are ticked. */
  #syncSwitches() {
    const consent = this.#api?.currentVisitorConsent?.() ?? {};
    for (const purpose of SWITCHES) {
      const input = this.querySelector(`input[name="${purpose}"]`);
      if (input) input.checked = consent[purpose] === 'yes';
    }
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const button = event.target instanceof Element ? event.target.closest('button[name]') : null;
    if (!button || !this.contains(button)) return;

    switch (button.name) {
      case 'settings':
        this.#show('settings');
        this.querySelector('input[name="analytics"]')?.focus();
        break;
      case 'close':
        this.#hide();
        break;
      case 'accept':
      case 'reject':
      case 'save':
        this.#record(consentFor(button.name, {
          analytics: this.querySelector('input[name="analytics"]')?.checked,
          marketing: this.querySelector('input[name="marketing"]')?.checked,
        }));
        break;
      default:
    }
  }

  /**
   * Escape closes Cookie settings opened on request. A needed choice stays open.
   * @param {KeyboardEvent} event
   */
  #onKeydown(event) {
    if (event.key !== 'Escape' || this.hidden || this.#asking) return;
    event.stopPropagation();
    this.#hide();
  }

  /**
   * Any link to #cookie-settings (the footer's Cookie settings) opens it in place, on any page.
   * @param {MouseEvent} event
   */
  #onLinkClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return;
    }
    if (url.hash !== OPEN_HASH) return;

    event.preventDefault();
    this.open(link);
  }

  /**
   * Records a choice with Shopify and closes. In the theme editor it only closes.
   * @param {{ analytics: boolean, marketing: boolean, preferences: boolean }} consent
   */
  #record(consent) {
    const api = this.#api;
    this.#hide();
    if (window.Shopify?.designMode) return;

    if (typeof api?.setTrackingConsent !== 'function') {
      console.error('lm-consent: the Customer Privacy API is not available, so the choice was not saved');
      return;
    }

    try {
      api.setTrackingConsent(consent, (error) => {
        if (error) console.error('lm-consent: the choice was not saved', error);
      });
    } catch (error) {
      console.error('lm-consent: the choice was not saved', error);
    }
  }

  /**
   * @param {string} name
   * @returns {HTMLButtonElement | null}
   */
  #button(name) {
    return this.querySelector(`button[name="${name}"]`);
  }
}

if (!customElements.get('lm-consent')) customElements.define('lm-consent', LmConsent);
